import React, { useState, useRef } from 'react';
import { Camera, Save, Trash2, Edit2, X, FileJson, RefreshCw, Settings, Image as ImageIcon, Layers, HelpCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { SYSTEM_PROMPT } from './systemPrompt';


export default function AlcoholLensProV2() {
  const [items, setItems] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);

  // 現在撮影中のセッション
  const [currentSessionImages, setCurrentSessionImages] = useState([]);
  const [showSessionModal, setShowSessionModal] = useState(false);

  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // デフォルトモデル
  const [modelName, setModelName] = useState("gemini-3.1-flash-lite-preview");
  const [availableModels, setAvailableModels] = useState([]);

  const [showSettings, setShowSettings] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [helpContent, setHelpContent] = useState('');
  const [loadingModels, setLoadingModels] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // 画像圧縮ロジック
  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const MAX_SIZE = 1600;
          if (width > height) {
            if (width > MAX_SIZE) { height *= MAX_SIZE / width; width = MAX_SIZE; }
          } else {
            if (height > MAX_SIZE) { width *= MAX_SIZE / height; height = MAX_SIZE; }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
          resolve(dataUrl);
        };
      };
    });
  };

  const openHelp = async () => {
    if (!helpContent) {
      const res = await fetch('/README.md');
      const text = await res.text();
      setHelpContent(text);
    }
    setShowHelp(true);
  };

  // モデル一覧取得機能
  const fetchModels = async () => {
    setLoadingModels(true);
    try {
      const devApiKey = process.env.REACT_APP_GEMINI_API_KEY;
      const response = devApiKey
        ? await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${devApiKey}`)
        : await fetch('/api/models');
      const data = await response.json();
      if (data.error) throw new Error(data.error.message || JSON.stringify(data.error));

      const raw = data.models || [];
      const models = devApiKey
        ? raw.filter(m => m.supportedGenerationMethods?.includes('generateContent')).map(m => m.name.replace('models/', ''))
        : raw;
      setAvailableModels(models);

      // 優先順位: Lite > Flash > Pro
      const lite = models.find(m => m.includes("flash-lite"));
      const flash = models.find(m => m.includes("flash"));
      const bestModel = lite || flash || models[0];

      if (bestModel && !models.includes(modelName)) {
        setModelName(bestModel);
      }
      alert(`利用可能なモデルを ${models.length} 件取得しました。`);
    } catch (error) {
      alert("モデル一覧の取得に失敗しました。\n" + error.message);
    } finally {
      setLoadingModels(false);
    }
  };


  // 複数画像を解析する関数
  const analyzeImages = async (base64Images) => {
    setAnalyzing(true);
    const startTime = Date.now();
    try {
      const devApiKey = process.env.REACT_APP_GEMINI_API_KEY;
      let data;

      if (devApiKey) {
        const imageParts = base64Images.map(img => ({
          inline_data: { mime_type: 'image/jpeg', data: img.split(',')[1] }
        }));
        const response = await fetch(
          `https://generativelanguage.googleapis.com/v1beta/models/${modelName}:generateContent?key=${devApiKey}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              system_instruction: { parts: [{ text: SYSTEM_PROMPT }] },
              contents: [{ parts: imageParts }],
              generationConfig: { response_mime_type: 'application/json', maxOutputTokens: 1024 }
            })
          }
        );
        const result = await response.json();
        if (result.error) throw new Error(result.error.message || JSON.stringify(result.error));
        const text = result.candidates?.[0]?.content?.parts?.[0]?.text || '';
        const match = text.match(/\{[\s\S]*\}/);
        if (!match) throw new Error('No JSON object found in model response');
        data = JSON.parse(match[0]);
      } else {
        const response = await fetch('/api/analyze', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ base64Images, modelName })
        });
        data = await response.json();
        if (data.error) throw new Error(data.error);
      }

      const analyzeMs = Date.now() - startTime;
      setEditingItem({
        id: Date.now(),
        images: base64Images,
        analyzeMs,
        modelUsed: modelName,
        ...data
      });

      // セッションクリア
      setCurrentSessionImages([]);
      setShowSessionModal(false);
    } catch (error) {
      alert("解析エラー: " + error.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const compressed = await compressImage(file);
        
        // 最大2枚までの制限チェック（念のため）
        if (currentSessionImages.length >= 2) {
            alert("画像は最大2枚までです。");
            e.target.value = '';
            return;
        }

        // セッションに追加
        const newImages = [...currentSessionImages, compressed];
        setCurrentSessionImages(newImages);
        setShowSessionModal(true); // プレビューモードへ
      } catch (e) { alert("画像処理エラー"); }
      e.target.value = '';
    }
  };

  const saveItem = (item) => {
    setItems(prev => [item, ...prev]);
    setEditingItem(null);
  };

  const downloadJSON = () => {
    // 画像データを除外して軽量化
    const exportData = items.map(({ images, ...rest }) => rest);
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `alcohol_master_${new Date().toISOString().slice(0,10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans pb-32">
      <header className="bg-blue-700 text-white p-4 shadow-md sticky top-0 z-10">
        <div className="flex justify-between items-center max-w-md mx-auto">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Layers className="w-6 h-6" /> AlcoholLens v2
          </h1>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowSettings(!showSettings)} className="text-xs bg-blue-800 px-2 py-1 rounded flex items-center gap-1">
              <Settings className="w-4 h-4" /> 設定
            </button>
            <button onClick={openHelp} className="text-xs bg-blue-800 px-2 py-1 rounded flex items-center gap-1">
              <HelpCircle className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {showHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-4">
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white rounded-t-lg">
              <h2 className="text-lg font-bold text-blue-700 flex items-center gap-2"><HelpCircle className="w-5 h-5" /> ヘルプ</h2>
              <button onClick={() => setShowHelp(false)} className="text-slate-400 hover:text-slate-600"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-6 prose prose-sm max-w-none overflow-y-auto" style={{maxHeight: '75vh'}}>
              <ReactMarkdown>{helpContent}</ReactMarkdown>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-md mx-auto p-4">

        {/* 設定エリア */}
        {showSettings && (
          <div className="bg-white p-4 rounded-lg shadow mb-4 border border-blue-100 space-y-3">
             <div>
              <div className="flex justify-between items-end mb-1">
                <label className="block text-xs font-bold text-slate-500">使用モデル (Engine)</label>
                <button 
                  onClick={fetchModels}
                  disabled={loadingModels}
                  className="text-xs text-blue-600 flex items-center gap-1 hover:underline disabled:text-slate-400"
                >
                  <RefreshCw className={`w-3 h-3 ${loadingModels ? 'animate-spin' : ''}`} />
                  一覧取得
                </button>
              </div>
              
              {availableModels.length > 0 ? (
                <select 
                  value={modelName}
                  onChange={(e) => setModelName(e.target.value)}
                  className="w-full p-2 border rounded bg-white text-sm"
                >
                  {availableModels.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              ) : (
                <input 
                  type="text"
                  value={modelName}
                  onChange={(e) => setModelName(e.target.value)}
                  placeholder="例: gemini-2.0-flash-lite-preview-02-05"
                  className="w-full p-2 border rounded bg-slate-50 text-sm"
                />
              )}
              <p className="text-xs text-slate-400 mt-1">
                ※現在: <span className="font-mono font-bold text-slate-600">{modelName}</span> を使用中
              </p>
            </div>
          </div>
        )}

        {/* リスト表示 */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <h2 className="text-sm font-bold text-slate-500">スキャン履歴</h2>
            <button onClick={downloadJSON} disabled={items.length === 0} className="text-xs bg-emerald-600 text-white px-3 py-1 rounded flex items-center gap-1">
              <FileJson className="w-3 h-3" /> JSON出力
            </button>
          </div>
          
          {items.length === 0 ? (
            <div className="text-center py-10 text-slate-400 border border-dashed rounded-lg">
              <Camera className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>表面と裏面を撮影して<br/>高精度にデータ化</p>
            </div>
          ) : (
            items.map((item) => (
              <div key={item.id} className="bg-white p-4 rounded-lg shadow-sm border border-slate-100">
                <div className="flex gap-4 mb-2">
                  {/* 1枚目のサムネイルのみ表示 */}
                  <div className="w-16 h-16 bg-slate-100 rounded overflow-hidden flex-shrink-0 relative">
                    <img src={item.images[0]} className="w-full h-full object-cover" alt="thumb" />
                    {item.images.length > 1 && (
                      <div className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] px-1 font-bold">+{item.images.length - 1}</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-500 mb-0.5">{item.manufacturer}</p>
                    <h3 className="font-bold text-lg leading-tight truncate">{item.product_name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs bg-slate-100 px-2 py-0.5 rounded font-mono">{item.jan_code || "JAN未検出"}</span>
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-bold">{item.alcohol_content || "-%"}</span>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-center mt-1">
                  {item.analyzeMs && (
                    <span className="text-[10px] text-slate-300 font-mono">⏱ {(item.analyzeMs / 1000).toFixed(2)}s · {item.modelUsed}</span>
                  )}
                  <button onClick={() => setItems(prev => prev.filter(i => i.id !== item.id))} className="text-slate-300 hover:text-red-500 text-xs flex items-center gap-1 ml-auto">
                    <Trash2 className="w-3 h-3" /> 削除
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      {/* フローティングアクションボタン (メイン画面) */}
      <div className="fixed bottom-6 left-0 right-0 flex justify-center items-center gap-6 z-20">
        <button onClick={() => galleryInputRef.current.click()} disabled={analyzing} className="w-14 h-14 rounded-full shadow-lg bg-white text-blue-600 flex items-center justify-center">
          <ImageIcon className="w-6 h-6" />
        </button>
        <button onClick={() => cameraInputRef.current.click()} disabled={analyzing} className="w-18 h-18 rounded-full shadow-xl bg-blue-600 text-white flex items-center justify-center border-4 border-white" style={{width:'4.5rem', height:'4.5rem'}}>
          <Camera className="w-8 h-8" />
        </button>
        
        {/* Inputタグは共通のrefを使用 */}
        <input type="file" accept="image/*" capture="environment" ref={cameraInputRef} className="hidden" onChange={handleImageSelect} />
        <input type="file" accept="image/*" ref={galleryInputRef} className="hidden" onChange={handleImageSelect} />
      </div>

      {/* 撮影セッションモーダル（修正：画像追加ボタンを2つに分割） */}
      {showSessionModal && (
        <div className="fixed inset-0 bg-black/80 z-30 flex flex-col justify-end pb-8">
          <div className="p-4 text-white text-center">
            <h3 className="text-lg font-bold mb-2">撮影画像を確認 ({currentSessionImages.length}/2)</h3>
            <p className="text-sm opacity-80 mb-4">裏面（バーコード）なども追加できます</p>
            
            {/* プレビュー横並び */}
            <div className="flex gap-2 justify-center mb-6 overflow-x-auto px-4">
              {currentSessionImages.map((img, idx) => (
                <div key={idx} className="w-20 h-20 rounded border-2 border-white overflow-hidden flex-shrink-0 relative">
                  <img src={img} className="w-full h-full object-cover" alt={`shot-${idx}`} />
                  <button 
                    onClick={() => setCurrentSessionImages(prev => prev.filter((_, i) => i !== idx))}
                    className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              
              {/* 追加ボタンエリア：2枚未満のときだけ表示 */}
              {currentSessionImages.length < 2 && (
                <div className="flex flex-col gap-2">
                  {/* カメラで追加 */}
                  <button 
                    onClick={() => cameraInputRef.current.click()}
                    className="w-20 h-9 rounded border border-white/50 flex items-center justify-center text-white bg-blue-600 hover:bg-blue-500"
                  >
                    <Camera className="w-4 h-4 mr-1" />
                    <span className="text-[10px] font-bold">撮影</span>
                  </button>
                  {/* アルバムから追加 */}
                  <button 
                    onClick={() => galleryInputRef.current.click()}
                    className="w-20 h-9 rounded border border-white/50 flex items-center justify-center text-blue-600 bg-white hover:bg-slate-100"
                  >
                    <ImageIcon className="w-4 h-4 mr-1" />
                    <span className="text-[10px] font-bold">選択</span>
                  </button>
                </div>
              )}
            </div>

            <button 
              onClick={() => analyzeImages(currentSessionImages)}
              disabled={analyzing || currentSessionImages.length === 0}
              className="w-full max-w-xs mx-auto py-3 bg-blue-500 hover:bg-blue-400 text-white font-bold rounded-full shadow-lg flex items-center justify-center gap-2"
            >
              {analyzing ? (
                <>
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                  解析中...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" />
                  まとめて解析する
                </>
              )}
            </button>
            
            <button onClick={() => {setShowSessionModal(false); setCurrentSessionImages([])}} className="mt-4 text-sm text-white/60 underline">
              キャンセル
            </button>
          </div>
        </div>
      )}

      {/* 結果編集モーダル */}
      {editingItem && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl flex flex-col max-h-[90vh]">
            <div className="bg-slate-100 p-4 border-b flex justify-between items-center">
              <h3 className="font-bold text-slate-700 flex items-center gap-2"><Edit2 className="w-4 h-4" /> 解析結果</h3>
              <button onClick={() => setEditingItem(null)}><X className="w-6 h-6" /></button>
            </div>
            
            <div className="p-4 overflow-y-auto space-y-4">
              <div className="flex gap-2 overflow-x-auto pb-2">
                 {editingItem.images.map((img, i) => (
                   <img key={i} src={img} className="h-32 rounded object-contain bg-slate-100 border" alt="res" />
                 ))}
              </div>
              {editingItem.analyzeMs && (
                <div className="flex gap-2 text-xs text-slate-400 font-mono">
                  <span>⏱ {(editingItem.analyzeMs / 1000).toFixed(2)}s</span>
                  <span className="text-slate-300">|</span>
                  <span className="truncate">{editingItem.modelUsed}</span>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">JANコード</label>
                  <input type="text" value={editingItem.jan_code || ""} onChange={(e) => setEditingItem({...editingItem, jan_code: e.target.value})} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg font-mono text-lg" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">商品名</label>
                  <input type="text" value={editingItem.product_name || ""} onChange={(e) => setEditingItem({...editingItem, product_name: e.target.value})} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg font-bold" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-1">メーカー</label>
                    <input type="text" value={editingItem.manufacturer || ""} onChange={(e) => setEditingItem({...editingItem, manufacturer: e.target.value})} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-blue-600 mb-1">アルコール度数</label>
                    <input type="text" value={editingItem.alcohol_content || ""} onChange={(e) => setEditingItem({...editingItem, alcohol_content: e.target.value})} className="w-full p-3 bg-blue-50 border border-blue-200 text-blue-800 font-bold rounded-lg text-center" />
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t">
              <button onClick={() => saveItem(editingItem)} className="w-full py-3 bg-blue-600 text-white font-bold rounded shadow flex items-center justify-center gap-2">
                <Save className="w-5 h-5" /> リストに追加保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
